import { Injectable } from '@angular/core';
import { catchError, map } from 'rxjs/operators';
import { APIService } from '../api/api.service'
import { LocalStorageService } from '../local-storage/local-storage.service';
import { UserModel } from '../../models/user.model';
import { DeviceService } from '../device/device.service';
import { AppearanceService } from '../appearance/appearance.service';
import { Constants } from '../../constants/constants';
import { ClientService } from '../client/client.service';
import { throwError } from 'rxjs';


@Injectable({
  providedIn: 'root'
})
export class UserService 
{
  public user: UserModel;
  passwordResetInProgress = false;
 
  private apiEndpointMode = null;


  constructor(private apiService: APIService, private localStorageService: LocalStorageService,
    private appearanceService: AppearanceService, private deviceService: DeviceService,
    private clientService: ClientService) 
  {
    this.initService();
  }


  initService()
  {
    this.apiService.baseUrl = Constants.PROD_URL;

    this.appearanceService.listenForEvent('storage:loaded', (data: any) => {

      this.loadFromLocalData();

    });
  }


  loadFromLocalData() 
  {
    this.passwordResetInProgress = this.localStorageService.isPasswordResetInProgress();
    this.apiEndpointMode = this.localStorageService.getApiEndpointMode();
    this.apiService.baseUrl = this.getApiEndPoint();

    this.user = this.localStorageService.getUser();
    
    if (this.user)
    {
      this.apiService.id = this.user.sessionId;
      this.clientService.loadFromLocalData();
    }
  }


  // API Calls -------------------------------------------------------------------------


  public loginUser(email, password): any 
  { 
    this.user = null;

    var data =
    {
      Email: email,
      Password: password,
      DeviceUuid: this.deviceService.getDeviceUuid(),
      DeviceModel: this.deviceService.getDeviceModel(),
      DeviceType: this.deviceService.getDeviceType(),
      DeviceOS: this.deviceService.getDeviceOs()
    }

    return this.apiService.loginUser(data).pipe(map((response: any) => {

        if (response.Success)
        {
          this.user               = new UserModel();
          this.user.userUid       = Number(response.UserUid);
          this.user.email         = response.UserEmail;
          this.user.sessionId     = response.SessionId;
          this.user.deviceUid     = Number(response.DeviceUid);
          this.user.userRoleName  = response.UserRoleName;
          this.user.userRoleUid   = Number(response.UserRoleUid);
          this.user.firstName     = response.firstName;
          this.user.lastName      = response.lastName;
          this.user.addressOne    = response.addressOne;
          this.user.addressTwo    = response.addressTwo;
          this.user.city          = response.addressCity;
          this.user.postCode      = response.addressPostCode;

          this.user.testDeviceSerialNumber= response.testDeviceSerialNumber;

          this.user.permissionUids = [];

          for (var i = 0; i < response.Permissions.length; i++)
          {
            var uid = Number(response.Permissions[i].PermissionUid);
            this.user.permissionUids.push(uid);
          }          
        }
      
        return response;
      }),
      catchError((err) => { return throwError(() => err);  })
    );
  }


  public updateUser(payload): any 
  { 
    return this.apiService.updateUser(payload).pipe(map((response: any) => {

        if (response.Success)
        {
          
        }
      
        return response;
      }),
      catchError((err) => { return throwError(() => err);  })
    );
  }


  public editUserProfile(updatedUser): any 
  { 
    return this.apiService.editUserProfile(updatedUser).pipe(map((response: any) => {

        if (response.Success)
        {
          this.user.firstName   = updatedUser.firstName;
          this.user.lastName    = updatedUser.lastName;
          this.user.addressOne  = updatedUser.addressOne;
          this.user.addressTwo  = updatedUser.addressTwo;
          this.user.city        = updatedUser.city;
          this.user.postCode    = updatedUser.postCode;
          this.user.testDeviceSerialNumber = updatedUser.testDeviceSerialNumber;

          this.localStorageService.saveUser(this.user);
        }
      
        return response;
      }),
      catchError((err) => { return throwError(() => err); })
    );
  }


  public loginComplete()
  {
    this.deviceService.updateDeviceUid(this.user.deviceUid);
    this.localStorageService.saveUser(this.user);
  }


  public updateLocalSessionUser(updatedUser)
  {
    this.user.firstName     = updatedUser.firstName;
    this.user.lastName      = updatedUser.lastName;
    this.user.addressOne    = updatedUser.addressOne;
    this.user.addressTwo    = updatedUser.addressTwo;
    this.user.city          = updatedUser.city;
    this.user.postCode      = updatedUser.postCode;
    this.user.testDeviceSerialNumber = updatedUser.testDeviceSerialNumber;
    this.localStorageService.saveUser(this.user);
  }


  public logoutUser() 
  {
    if (this.deviceService.isDeviceLoggedIn())
    {
      var data = {
        DeviceUid: this.getDeviceUid()
      }

      this.apiService.logoutUser(data).subscribe(data => { },
      err => { });
    }

    this.user = null;
    this.apiService.id = null;

    var currentDevice = this.deviceService.getCurrentDevice();

    this.localStorageService.clearAll();
    this.localStorageService.saveUser(this.user);
    this.localStorageService.saveClient(null);
    this.clientService.changeCurrentOrganisation(null);
    this.localStorageService.saveEngineerStatsFilter(null);
    this.localStorageService.saveDevice(currentDevice);

    this.appearanceService.triggerEvent('user:loggedout', {
      time: new Date()
    });
  }


  public sendPasswordResetEmail(email) 
  {
    var payload = {
      userEmail: email
    }

    return this.apiService.sendPasswordResetEmail(payload).pipe(map((response: any) => {

      if (response.Success)
      {
        this.passwordResetInProgress = true;
        this.localStorageService.passwordResetInProgress(true);
      }
    
      return response;
    }),
      catchError((err) => { return throwError(() => err);  })
    );
  }


  public changePasswordUsingEmailCode(email, newPassword, verificationCode) 
  {
    var payload = {
      userEmail: email,
      newPassword: newPassword,
      verificationCode: verificationCode
    }

    return this.apiService.changePasswordUsingEmailCode(payload).pipe(map((response: any) => {

      if (response.Success)
      {
        this.passwordResetInProgress = false;
        this.localStorageService.passwordResetInProgress(false);
      }
    
      return response;
    }),
      catchError((err) => { return throwError(() => err);  })
    );
  }


  public userChangedPassword(newPassword) 
  {
    var payload = {
      newPassword: newPassword
    }

    return this.apiService.userChangedPassword(payload).pipe(map((response: any) => {

      if (response.Success)
      {

      }
    
      return response;
    }),
      catchError((err) => { return throwError(() => err);  })
    );
  }


  public getEngineerStatistics(payload) 
  {
    return this.apiService.getEngineerStatistics(payload).pipe(map((response: any) => {

      if (response.Success)
      {

      }
    
      return response;
    }),
      catchError((err) => { return throwError(() => err);  })
    );
  }


  public getEngineerSimpleStatistics(payload) 
  {
    return this.apiService.getEngineerSimpleStatistics(payload).pipe(map((response: any) => {

      if (response.Success)
      {

      }
    
      return response;
    }),
      catchError((err) => { return throwError(() => err);  })
    );
  }


  public getEngineerStatisticsRepairList(payload) 
  {
    return this.apiService.getEngineerStatisticsRepairList(payload).pipe(map((response: any) => {

      if (response.Success)
      {

      }
    
      return response;
    }),
      catchError((err) => { return throwError(() => err);  })
    );
  }


  public getEngineerStatisticsRecommendedList(payload) 
  {
    return this.apiService.getEngineerStatisticsRecommendedList(payload).pipe(map((response: any) => {

      if (response.Success)
      {

      }
    
      return response;
    }),
      catchError((err) => { return throwError(() => err);  })
    );
  }


  public cancelResetPassword()
  {
    this.passwordResetInProgress = false;
    this.localStorageService.passwordResetInProgress(false);
  }


  public deleteUserFile(fileUid, clientUid, organisationUid): any 
  { 
    var payload = {
      FileUid: fileUid,
      ClientUid: clientUid,
      OrganisationUid: organisationUid
    }

    return this.apiService.deleteUserFile(payload).pipe(map((response: any) => {

        return response;
      }),
      catchError((err) => { return throwError(() => err);  })
    );
  }

  public downloadFile(data): any 
  { 
    return this.apiService.downloadUserFile(data).pipe(map((response: any) => {

        return response;
      }),
      catchError((err) => { return throwError(() => err);  })
    );
  }

  public getStockOrderDetails(data): any 
  { 
    return this.apiService.getStockOrderDetails(data).pipe(map((response: any) => {

        return response;
      }),
      catchError((err) => { return throwError(() => err);  })
    );
  }

  public getStockPresetDetails(data): any 
  { 
    return this.apiService.getStockPresetDetails(data).pipe(map((response: any) => {

        return response;
      }),
      catchError((err) => { return throwError(() => err);  })
    );
  }

  public createNewStockPreset(data): any 
  { 
    return this.apiService.createNewStockPreset(data).pipe(map((response: any) => {

        return response;
      }),
      catchError((err) => { return throwError(() => err);  })
    );
  }

  public updateStockPresetStock(data): any 
  { 
    return this.apiService.updateStockPresetStock(data).pipe(map((response: any) => {

        return response;
      }),
      catchError((err) => { return throwError(() => err);  })
    );
  }

  public updateStockPresetAssetType(data): any 
  { 
    return this.apiService.updateStockPresetAssetType(data).pipe(map((response: any) => {

        return response;
      }),
      catchError((err) => { return throwError(() => err);  })
    );
  }

  public updateStockPresetRemedial(data): any 
  { 
    return this.apiService.updateStockPresetRemedial(data).pipe(map((response: any) => {

        return response;
      }),
      catchError((err) => { return throwError(() => err);  })
    );
  }

  public markStockOrderAsPurchased(data): any 
  { 
    return this.apiService.markStockOrderAsPurchased(data).pipe(map((response: any) => {

        return response;
      }),
      catchError((err) => { return throwError(() => err);  })
    );
  }

  public markStockOrderAsShipped(data): any 
  { 
    return this.apiService.markStockOrderAsShipped(data).pipe(map((response: any) => {

        return response;
      }),
      catchError((err) => { return throwError(() => err);  })
    );
  }


  // Getters -------------------------------------------------------------------------


  public isInternalStaff(): boolean
  {
    if (this.user)
    {
      if (this.user.userRoleUid == Constants.USERROLE_STAFF)
      {
        return true;
      }
    }

    return false;
  }

  public getApiEndPointMode(): number
  {
    if (this.apiEndpointMode)
    {
        return this.apiEndpointMode;
    }

    return Constants.API_PROD;
  }


  public getNavBarText(): string
  {
    if (this.isDevMode())
    {
      return "[DEV]";
    }

    return ""
  }


  public isDevMode(): boolean
  {
    if (this.apiEndpointMode)
    {
        if (this.apiEndpointMode == Constants.API_DEV)
        {
          return true;
        }
    }

    return false;
  }


  public getApiEndPoint(): string
  {
    if (this.apiEndpointMode)
    {
      if (this.apiEndpointMode == Constants.API_PROD)
      {
        return Constants.PROD_URL;
      }
      else if (this.apiEndpointMode == Constants.API_DEV)
      {
        return Constants.DEV_URL;
      }
    }

    return Constants.PROD_URL;
  }


  public saveApiEndpointMode(mode)
  {
    if (this.user)
    {
      this.logoutUser();
    }
    
    this.apiEndpointMode = mode;
    this.apiService.baseUrl = this.getApiEndPoint();
    this.localStorageService.saveApiEndpointMode(this.apiEndpointMode);
  }

  

  public getUserEmail(): string
  {
    if (this.user)
    {
      return this.user.email;
    }

    return null;
  }

  
  public isUserLoggedIn(): boolean
  {
    if (this.user)
    {
        return true;
    }

    return false;
  }


  public getUserUid(): number
  {
    if (this.user)
    {
      return this.user.userUid;
    }
    
    return null;
  }

  public getSessionId(): string
  {
    if (this.user)
    {
      return this.user.sessionId;
    }

    return null;
  }


  public getUserName(): string
  {
    if (this.user)
    {
      return this.user.firstName + " " + this.user.lastName;
    }

    return null;
  }


  public getEmailAddress(): string
  {
    if (this.user)
    {
      if (this.user.email)
      {
        if (this.user.email != "")
        {
          return this.user.email;
        }
      }
    }
    
    return null;
  }


  public getDeviceUid(): number
  {
    if (this.user)
    {
      return this.user.deviceUid;
    }

    return null;
  }


  public getUser(): UserModel
  {
    if (this.user)
    {
      return this.user;
    }

    return null;
  }


  public isPasswordResetInProgress(): boolean
  {
    return this.passwordResetInProgress;
  }


  toggleTheme()
  {
    var light = this.appearanceService.isLightThemeEnabled();
    light = !light;
    this.appearanceService.setTheme(light);
    this.localStorageService.setLightThemeEnabled(light);
  }

  public getCurrentPageLength(page)
  {
    return this.localStorageService.getCurrentPageLength(page);
  }

  public saveCurrentPageLength(count, page)
  {
    this.localStorageService.saveCurrentPageLength(count, page);
  }


  public getCurrentPageCount(page)
  {
    return this.localStorageService.getCurrentPageCount(page);
  }

  public saveCurrentPageCount(count, page)
  {
    this.localStorageService.saveCurrentPageCount(count, page);
  }

  public getColumnVisibilty(page)
  {
    return this.localStorageService.getColumnVisibility(page);
  }

  public saveColumnVisibilty(columns, page)
  {
    this.localStorageService.saveColumnVisibility(columns, page);
  }

  public getSelectedRowsForPage(page)
  {
    return this.localStorageService.getSelectedRowsForPage(page);
  }

  public saveSelectedRowsForPage(rows, page)
  {
    this.localStorageService.saveSelectedRowsForPage(rows, page);
  }

  public getEngineerStatsFilter()
  {
    return this.localStorageService.getEngineerStatsFilter();
  }

  public saveEngineerStatsFilter(options)
  {
    this.localStorageService.saveEngineerStatsFilter(options);
  }
}
